import React, { useState, useEffect, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useReadingContextSafe } from '../../contexts/ReadingContext';
import { useMessages } from '../../hooks/useMessages';
import { useShopping } from '../../hooks/useShopping';
import { contextEngine } from '../../lib/context-engine';
import { contextFusionService } from '../../lib/context-fusion-service';
import { familyCoordinationService } from '../../lib/family-coordination-service';
import { transportationService } from '../../lib/transportation-service';
import { localEventsService } from '../../lib/local-events-service';
import { format, isToday, isTomorrow, addDays, differenceInDays } from 'date-fns';
import { 
  Calendar, 
  Star, 
  DollarSign, 
  GraduationCap, 
  Lightbulb,
  Check,
  Clock,
  Book,
  ShoppingCart,
  Home,
  TrendingUp,
  Flame,
  AlertCircle,
  Plus,
  ChevronRight,
  X,
  Cloud,
  Sun,
  CloudRain,
  Thermometer,
  MapPin,
  BarChart3,
  Brain
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import ViewSwitcher from '../Shared/ViewSwitcher';
import DeveloperTestingPanel from './DeveloperTestingPanel';
import WeatherLocationConfig from './WeatherLocationConfig';
import FamilyCoordinationCard from './FamilyCoordinationCard';
import TransportationCard from './TransportationCard';
import LocalEventsCard from './LocalEventsCard';
import UnifiedBig3Planner from '../Planning/UnifiedBig3Planner';
import { useBig3Integration } from '../../hooks/useBig3Integration';
import './IntelligenceDashboardV2.css';

/**
 * PaperSedge Intelligence Dashboard V2
 * Clean, actionable personal command center inspired by the mockup
 */
export default function IntelligenceDashboardV2() {
  const { 
    currentUser, 
    family, 
    familyCalendar, 
    familyActivities,
    messages,
    tasks,
    shoppingItems,
    plans,
    allowanceJobs
  } = useFamilyContext();

  const readingContext = useReadingContextSafe();
  const { messages: familyMessages } = useMessages({ recipientId: currentUser?.id });
  const { items: allShoppingItems } = useShopping();

  const navigate = useNavigate();

  const [context, setContext] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [contextOverride, setContextOverride] = useState(null);
  const [energyLevel, setEnergyLevel] = useState('normal');
  const [showWeatherConfig, setShowWeatherConfig] = useState(false);
  const [fusedContext, setFusedContext] = useState(null);
  const [coordinationAnalysis, setCoordinationAnalysis] = useState(null);
  const [transportationAnalysis, setTransportationAnalysis] = useState(null);
  const [localEvents, setLocalEvents] = useState([]);
  const [activityRecommendations, setActivityRecommendations] = useState([]);

  const isParent = currentUser?.isParent;
  const userType = isParent ? 'parent' : 'teen';

  // Build contextual intelligence with smart timing and fusion
  useEffect(() => {
    const buildIntelligence = async () => {
      if (!currentUser) return;

      setIsLoading(true);
      try {
        // Initialize pattern sync for cross-device persistence
        await contextEngine.initializePatternSync(currentUser.id);
        const familyData = {
          family,
          familyCalendar,
          familyActivities,
          messages,
          tasks,
          shoppingItems,
          plans,
          allowanceJobs
        };

        // Build base context
        const currentContext = await contextEngine.buildContext(currentUser.id, familyData);
        const finalContext = contextOverride || currentContext;
        
        // Phase 3: Context fusion and family coordination analysis
        let enhancedContext = finalContext;
        let coordination = null;
        
        // Define family members for use in multiple places
        const familyMembers = [...(family?.parents || []), ...(family?.kids || [])];
        
        if (!contextOverride) {
          // Perform context fusion
          const fused = await contextFusionService.fuseContexts(currentUser.id, familyData);
          setFusedContext(fused);
          enhancedContext = { ...finalContext, fusion: fused.fusion };
          
          // Analyze family coordination
          coordination = await familyCoordinationService.analyzeCoordination(
            familyMembers,
            familyCalendar,
            tasks,
            familyActivities
          );
          setCoordinationAnalysis(coordination);
          
          // Analyze transportation coordination
          const transportation = await transportationService.analyzeTransportationNeeds(
            familyData, 
            fused?.fusion?.weather
          );
          setTransportationAnalysis(transportation);
        }

        // Get local events and activity recommendations
        try {
          // Initialize local events service with family context
          localEventsService.initialize(familyMembers);
          
          // Get upcoming local events
          const events = await localEventsService.getLocalEvents({
            dateRange: 7,
            weather: fused?.fusion?.weather
          });
          setLocalEvents(events);

          // Get personalized activity recommendations
          const recommendations = await localEventsService.getActivityRecommendations({
            weather: fused?.fusion?.weather,
            timeOfDay: enhancedContext.temporal?.period,
            energyLevel: energyLevel,
            duration: 120 // 2 hours default
          });
          setActivityRecommendations(recommendations);
        } catch (error) {
          console.error('Error loading local events:', error);
          setLocalEvents([]);
          setActivityRecommendations([]);
        }
        
        // Generate suggestions with smart timing
        let contextualSuggestions = contextEngine.generateSuggestions(enhancedContext, userType, familyData);
        
        // Add fusion insights as suggestions
        if (enhancedContext.fusion?.insights) {
          const fusionSuggestions = enhancedContext.fusion.insights.map(insight => ({
            type: insight.type,
            priority: insight.priority,
            title: insight.title,
            description: insight.description,
            confidence: insight.confidence || 0.8,
            action: insight.actionable ? 'view-details' : null,
            metadata: { source: 'fusion', insight }
          }));
          contextualSuggestions.push(...fusionSuggestions);
        }
        
        // Add coordination optimizations as suggestions
        if (coordination?.optimizations) {
          const coordSuggestions = coordination.optimizations.slice(0, 3).map(opt => ({
            type: 'coordination',
            priority: opt.priority,
            title: `${opt.type === 'conflict-resolution' ? 'Resolve conflict' : 'Opportunity'}: ${opt.target.type}`,
            description: opt.resolutions?.[0]?.suggestion || opt.actions?.[0]?.suggestion || 'View details',
            confidence: 0.85,
            action: 'view-coordination',
            metadata: { optimization: opt }
          }));
          contextualSuggestions.push(...coordSuggestions);
        }
        
        // Add transportation recommendations as suggestions
        if (transportationAnalysis?.recommendations) {
          const transportSuggestions = transportationAnalysis.recommendations.slice(0, 2).map(rec => ({
            type: rec.type,
            priority: rec.priority,
            title: rec.title,
            description: rec.description,
            confidence: rec.confidence || 0.8,
            action: rec.action,
            metadata: { transportation: rec }
          }));
          contextualSuggestions.push(...transportSuggestions);
        }

        // Add local events and activity recommendations as suggestions
        if (activityRecommendations.length > 0) {
          const eventSuggestions = activityRecommendations.slice(0, 2).map(rec => ({
            type: 'local-activity',
            priority: rec.confidence >= 0.8 ? 'high' : 'medium',
            title: rec.title,
            description: rec.description,
            confidence: rec.confidence || 0.7,
            action: 'view-local-events',
            metadata: { localActivity: rec }
          }));
          contextualSuggestions.push(...eventSuggestions);
        }
        
        // Apply smart timing filters if not in testing mode
        if (!contextOverride) {
          contextualSuggestions = await contextEngine.filterSuggestionsByTiming(
            currentUser.id, 
            contextualSuggestions, 
            familyData
          );
        }

        setContext(enhancedContext);
        setSuggestions(contextualSuggestions);
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error building intelligence context:', error);
      } finally {
        setIsLoading(false);
      }
    };

    buildIntelligence();
    
    // Refresh context every 15 minutes
    const interval = setInterval(buildIntelligence, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, family, familyCalendar, familyActivities, messages, tasks, shoppingItems, plans, allowanceJobs, userType, contextOverride]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!currentUser) return { streak: 0, balance: 0, tasksToday: 0, newSuggestions: 0 };

    // Calculate streak (with sample data fallback)
    const streak = currentUser.streak || 7;

    // Calculate balance (with sample data fallback) 
    const balance = currentUser.balance || (isParent ? 0 : 47.50);

    // Calculate tasks today
    const tasksToday = tasks?.filter(task => 
      task.due_date && isToday(new Date(task.due_date))
    ).length || 2; // Sample: 2 tasks today

    // Count new suggestions
    const newSuggestions = suggestions.filter(s => 
      s.priority === 'high' || s.priority === 'critical'
    ).length || 3; // Sample: 3 new suggestions

    return { streak, balance, tasksToday, newSuggestions };
  }, [currentUser, tasks, suggestions, isParent]);

  // Get upcoming events
  const upcomingEvents = useMemo(() => {
    let events = [];
    
    if (familyCalendar && familyCalendar.length > 0) {
      const today = new Date();
      events = familyCalendar
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= today;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
    }
    
    // Add sample events if none exist
    if (events.length === 0) {
      const sampleEvents = [
        {
          id: 'sample-1',
          title: 'Chimney Rock State Park Hike',
          date: addDays(new Date(), 5).toISOString(),
          category: 'Family Event',
          time: 'All day'
        },
        {
          id: 'sample-2',
          title: 'Raleigh Pride Festival',
          date: addDays(new Date(), 7).toISOString(),
          category: 'Confirmed Family Event',
          time: '10:00 AM'
        }
      ];
      events = sampleEvents;
    }
    
    return events;
  }, [familyCalendar]);

  // Get new content recommendations
  const newContent = useMemo(() => {
    const content = [];
    
    // Book recommendations
    if (readingContext?.recommendations?.length > 0) {
      content.push({
        type: 'book',
        icon: Book,
        text: `New book recommendation: "${readingContext.recommendations[0].title}"`,
        time: '2h ago',
        action: () => navigate('/reading')
      });
    }

    // Activity ideas
    if (familyActivities?.length > 0) {
      const recent = familyActivities[familyActivities.length - 1];
      content.push({
        type: 'activity',
        icon: Star,
        text: `New weekend activity idea: "${recent.title}"`,
        time: '1d ago',
        action: () => {
          // Navigate to the specific activity if it has an ID, otherwise to family activities
          if (recent.id) {
            navigate(`/activities/${recent.id}`);
          } else {
            navigate('/family');
          }
        }
      });
    }

    // College updates
    if (!isParent) {
      content.push({
        type: 'college',
        icon: GraduationCap,
        text: 'College application workshop added to your journey',
        time: '3d ago',
        action: () => navigate('/college')
      });
    }

    return content.slice(0, 3);
  }, [readingContext, familyActivities, isParent, navigate]);


  // Handle suggestion interactions
  const handleSuggestionAction = (suggestion, action) => {
    if (context && currentUser) {
      contextEngine.trackUserPattern(currentUser.id, action, context, {
        suggestionType: suggestion.type,
        action,
        priority: suggestion.priority,
        timing: suggestion.timing
      });
    }
    
    // Execute suggestion action
    if (action === 'suggestion-accepted' && suggestion.action) {
      // Handle different suggestion actions
      switch (suggestion.action) {
        case 'view-priority-tasks':
          navigate('/tasks');
          break;
        case 'family-communication':
          navigate('/messages');
          break;
        case 'family-activity-planning':
          navigate('/family');
          break;
        case 'start-focus-session':
          // Focus session guidance
          const duration = suggestion.metadata?.suggestedDuration || '25 minutes';
          alert(`Focus Session\n\nStarting a ${duration} focus session!\n\nTips:\n• Find a quiet space\n• Put away distractions\n• Set a timer\n• Take breaks every 25-30 minutes\n\nConsider using focus apps like Forest, Focus Keeper, or your phone's built-in timer.`);
          break;
        case 'view-coordination':
          // Show detailed coordination analysis
          const coordination = suggestion.metadata?.optimization;
          if (coordination) {
            const details = `Coordination: ${coordination.type}\n` +
                          `Priority: ${coordination.priority}\n` +
                          `Target: ${coordination.target.type}\n` +
                          `Suggestion: ${coordination.resolutions?.[0]?.suggestion || coordination.actions?.[0]?.suggestion || 'View full analysis'}`;
            alert(`Family Coordination Opportunity\n\n${details}\n\nTip: Navigate to /family to explore coordination options further.`);
          }
          break;
        case 'view-details':
          // Show fusion insight details
          const insight = suggestion.metadata?.insight;
          if (insight) {
            const details = `${insight.title}\n\n${insight.description}`;
            alert(`Smart Insight\n\n${details}`);
          }
          break;
        case 'coordinate-carpool':
          alert('Carpool Coordination\n\nThis would help coordinate carpools with other families. Consider reaching out to other parents to organize shared transportation.');
          break;
        case 'optimize-route':
          alert('Route Optimization\n\nThis would suggest the most efficient route for multiple stops. Consider using navigation apps like Google Maps or Waze for real-time optimization.');
          break;
        case 'resolve-conflict':
          const conflict = suggestion.metadata?.transportation;
          if (conflict) {
            alert(`Schedule Conflict Resolution\n\n${conflict.description || conflict.title}\n\nConsider adjusting timing or finding alternative transportation arrangements.`);
          }
          break;
        case 'review-weather-impact':
          alert('Weather Impact Review\n\nThis would show how current weather affects your transportation plans. Check local weather forecasts and plan accordingly.');
          break;
        case 'coordinate-transport':
          alert('Transportation Coordination\n\nThis would help coordinate transportation needs across family members. Consider family calendar coordination and shared ride planning.');
          break;
        case 'view-local-events':
          // Show local events details and navigate
          const localActivity = suggestion.metadata?.localActivity;
          if (localActivity) {
            alert(`Local Activity Recommendation\n\n${localActivity.title}\n\n${localActivity.description}\n\nViewing all local events...`);
          }
          handleViewAllEvents();
          break;
        case 'suggest-library-visit':
        case 'check-library-hours':
          // Handle library suggestions
          console.log('Library suggestion:', suggestion.action, suggestion.title);
          alert(`Library suggestion: ${suggestion.title}\n\nConsider checking your local library's website or giving them a call to learn more about their current programs and hours.`);
          break;
        case 'coordinate-shopping-trip':
        case 'discuss-shopping-list':
          // Handle shopping coordination
          navigate('/shopping');
          break;
        case 'check-items-ready':
          navigate('/shopping');  
          break;
        default:
          // Generic suggestion action
          alert(`Smart Suggestion\n\n${suggestion.title}\n\n${suggestion.description}\n\nThis suggestion would help you ${suggestion.action.replace('-', ' ')} to improve your productivity and family coordination.`);
      }
    }
  };


  // Handle coordination details view
  const handleViewCoordinationDetails = () => {
    console.log('Coordination details:', coordinationAnalysis);
    if (coordinationAnalysis) {
      const conflicts = coordinationAnalysis.conflicts?.length || 0;
      const opportunities = coordinationAnalysis.opportunities?.length || 0;
      const score = coordinationAnalysis.coordinationScore || 0;
      alert(`Family Coordination Analysis\n\nCoordination Score: ${Math.round(score * 100)}%\nConflicts: ${conflicts}\nOpportunities: ${opportunities}\n\nThis analysis helps identify ways to improve family scheduling and coordination. Navigate to /family to explore coordination options.`);
    }
  };

  // Handle transportation details view
  const handleViewTransportationDetails = () => {
    console.log('Transportation details:', transportationAnalysis);
    if (transportationAnalysis) {
      const recs = transportationAnalysis.recommendations?.length || 0;
      alert(`Transportation Analysis\n\nRecommendations: ${recs}\n\nThis analysis identifies carpool opportunities and route optimizations. Consider coordinating with other families for shared transportation.`);
    }
  };

  // Handle local events interactions
  const handleViewEvent = (event) => {
    console.log('View event:', event);
    if (event) {
      const details = `Event: ${event.title}\n` +
                     `Date: ${event.date ? format(new Date(event.date), 'MMM d, yyyy') : 'TBD'}\n` +
                     `Time: ${event.time || 'All day'}\n` +
                     `Location: ${event.location || 'TBD'}\n` +
                     `Cost: ${event.cost || 'Free'}`;
      alert(`Local Event Details\n\n${details}\n\nThis would typically link to more event information or registration.`);
    }
  };

  const handleViewAllEvents = () => {
    console.log('View all local events');
    // For now, show an informative message about local events
    alert('Local Events\n\nThis feature would show comprehensive local community events, library programs, and seasonal activities.\n\nFor now, check the Local Events card in the sidebar for current recommendations, or visit your local library and community center websites directly.');
    // Could navigate to a dedicated local events page when implemented
    // navigate('/local-events');
  };

  // Handle family calendar event click
  const handleEventClick = (event) => {
    console.log('Family event clicked:', event);
    if (event) {
      const details = `${event.title}\n\n` +
                     `Date: ${format(new Date(event.date), 'EEEE, MMMM d, yyyy')}\n` +
                     `Time: ${event.time || 'All day'}\n` +
                     `Category: ${event.category || 'Event'}`;
      alert(`Family Event\n\n${details}\n\nNavigating to activities page for more details...`);
    }
    // Navigate to activities page where calendar events might be managed
    navigate('/activities');
  };

  // Handle context override for testing
  const handleContextOverride = (newContext) => {
    setContextOverride(newContext);
  };

  const handleRefresh = () => {
    setContextOverride(null);
    setLastUpdate(null);
  };

  const handleWeatherLocationUpdated = (newLocation, resolvedLocation) => {
    // Force refresh of context to get new weather data
    setLastUpdate(null);
    setShowWeatherConfig(false);
    
    // Track location change if we have context
    if (context && currentUser) {
      contextEngine.trackUserPattern(currentUser.id, 'location-updated', context, {
        newLocation,
        resolvedLocation
      });
    }
  };

  if (isLoading) {
    return (
      <div className="intelligence-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Building your command center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="intelligence-dashboard-v2">
      <div className="dashboard-container">
        <div className="main-content">
          {/* Personal Command Header */}
          <div className="command-header">
            <div className="header-top">
              <div className="header-greeting">Your Command Center</div>
              <ViewSwitcher />
            </div>
            <div className="header-subtitle">
              {context?.temporal.period === 'morning' && 'Good morning'}
              {context?.temporal.period === 'afternoon' && 'Good afternoon'}
              {context?.temporal.period === 'evening' && 'Good evening'}
              {' • '}
              {stats.streak > 5 ? `Looking good, ${currentUser?.name || 'there'}!` : `Welcome back, ${currentUser?.name || 'there'}`}
            </div>
            
            <div className="command-stats">
              <div className="command-stat">
                <div className="stat-number">{stats.streak}</div>
                <div className="stat-label">Day streak</div>
                <div className="stat-trend">
                  <Flame className="trend-icon" />
                  {stats.streak > 5 ? 'On fire!' : 'Keep going!'}
                </div>
              </div>
              <div className="command-stat">
                <div className="stat-number">${stats.balance}</div>
                <div className="stat-label">Your balance</div>
                <div className="stat-trend">
                  <DollarSign className="trend-icon" />
                  +$12 this week
                </div>
              </div>
              <div className="command-stat">
                <div className="stat-number">{stats.tasksToday}</div>
                <div className="stat-label">Tasks today</div>
                <div className="stat-trend">
                  <Check className="trend-icon" />
                  {stats.tasksToday === 0 ? 'All clear!' : 'You got this!'}
                </div>
              </div>
              <div className="command-stat">
                <div className="stat-number">{stats.newSuggestions}</div>
                <div className="stat-label">New suggestions</div>
                <div className="stat-trend">
                  <Lightbulb className="trend-icon" />
                  Check them out
                </div>
              </div>
            </div>
          </div>
          
          {/* Coming Up Events */}
          <div className="section-card">
            <div className="section-header">
              <Calendar className="section-icon" />
              <div className="section-title">Coming Up</div>
              {upcomingEvents.length > 3 && (
                <Link to="/calendar" className="section-link">View all</Link>
              )}
            </div>
            
            <div className="section-content">
              {upcomingEvents.length === 0 ? (
                <div className="empty-state">
                  <p>No upcoming events</p>
                  <Link to="/family" className="empty-action">Browse activities</Link>
                </div>
              ) : (
                upcomingEvents.slice(0, 3).map((event, index) => (
                  <EventItem 
                    key={event.id || index} 
                    event={event} 
                    onClick={handleEventClick} 
                  />
                ))
              )}
            </div>
          </div>
          
          {/* New for You */}
          <div className="section-card">
            <div className="section-header">
              <Star className="section-icon" />
              <div className="section-title">New for You</div>
              {newContent.length > 0 && (
                <div className="section-badge">{newContent.length} new</div>
              )}
            </div>
            
            <div className="section-content">
              {newContent.length === 0 ? (
                <div className="empty-state">
                  <p>No new content yet</p>
                  <span className="empty-subtitle">Check back soon!</span>
                </div>
              ) : (
                newContent.map((item, index) => (
                  <ContentItem key={index} item={item} />
                ))
              )}
            </div>
          </div>
          
          {/* Today's Focus - Big 3 - Using Main Dashboard Component */}
          <div className="section-card big3-wrapper">
            <UnifiedBig3Planner isMobile={false} />
          </div>
        </div>
        
        <div className="sidebar">
          {/* Weather Context */}
          {context?.environmental && (
            <WeatherCard 
              environmental={context.environmental} 
              onConfigureLocation={() => setShowWeatherConfig(true)}
            />
          )}
          
          {/* Money Tracker */}
          <MoneyCard 
            balance={stats.balance} 
            currentUser={currentUser} 
            allowanceJobs={allowanceJobs}
          />
          
          {/* Note: Family Coordination and Transportation cards removed for clarity */}
          {/* These generated too much confusing data without clear user value */}

          {/* Local Events */}
          <LocalEventsCard 
            localEvents={localEvents}
            recommendations={activityRecommendations}
            onViewEvent={handleViewEvent}
            onViewAllEvents={handleViewAllEvents}
          />

          {/* College Journey (for teens) */}
          {!isParent && <CollegeJourneyCard />}
          
          {/* Advanced Analytics */}
          <AdvancedAnalyticsCard patterns={context?.patterns} />

          {/* Smart Suggestions */}
          <SmartSuggestionsCard 
            suggestions={suggestions.slice(0, 3)} 
            onSuggestionAction={handleSuggestionAction}
          />
        </div>
      </div>

      {/* Developer Testing Panel */}
      <DeveloperTestingPanel
        context={context}
        onContextOverride={handleContextOverride}
        onRefresh={handleRefresh}
      />

      {/* Weather Location Configuration Modal */}
      {showWeatherConfig && (
        <WeatherLocationConfig
          onClose={() => setShowWeatherConfig(false)}
          onLocationUpdated={handleWeatherLocationUpdated}
        />
      )}
    </div>
  );
}

// Component: Event Item
function EventItem({ event, onClick }) {
  const eventDate = new Date(event.date);
  const isNearFuture = differenceInDays(eventDate, new Date()) <= 7;
  
  return (
    <div 
      className="event-item clickable" 
      onClick={() => onClick && onClick(event)}
      role="button"
      tabIndex={0}
    >
      <div className="event-date">
        <div className="event-day">{format(eventDate, 'EEE').toUpperCase()}</div>
        <div className="event-number">{format(eventDate, 'd')}</div>
      </div>
      <div className="event-content">
        <div className="event-title">{event.title}</div>
        <div className="event-meta">
          {event.category} • {event.time || 'All day'}
        </div>
      </div>
      {/* Only show action for events that aren't personal/family calendar items */}
      {event.source !== 'family' && event.requiresRSVP && (
        <Link to="/activities" className="event-action">
          {isNearFuture ? 'View Details' : 'RSVP'}
        </Link>
      )}
    </div>
  );
}

// Component: Content Item
function ContentItem({ item }) {
  const Icon = item.icon;
  
  return (
    <div className="content-item" onClick={item.action}>
      <div className="content-icon">
        <Icon size={12} />
      </div>
      <div className="content-text">{item.text}</div>
      <div className="content-time">{item.time}</div>
    </div>
  );
}


// Component: Money Card
function MoneyCard({ balance, currentUser, allowanceJobs = [] }) {
  // Calculate actual weekly earnings from recent jobs
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  const recentJobs = allowanceJobs.filter(job => 
    job.completed_at && new Date(job.completed_at) >= oneWeekAgo
  );
  
  const weeklyEarnings = recentJobs.reduce((sum, job) => sum + (job.amount || 0), 0);
  const defaultEarnings = weeklyEarnings > 0 ? weeklyEarnings : 12; // Fallback to $12
  
  const savingsGoal = 150; // TODO: Make this user-configurable
  const savingsProgress = Math.round((balance / savingsGoal) * 100);
  
  return (
    <div className="sidebar-card">
      <div className="sidebar-title">
        <DollarSign className="sidebar-icon" />
        <span>Your Money</span>
      </div>
      
      <div className="money-main">
        <div className="money-balance">${balance.toFixed(2)}</div>
        <div className="money-label">Current balance</div>
      </div>
      
      <div className="money-stats">
        <div className="money-stat">
          <div className="money-stat-value">${defaultEarnings}</div>
          <div className="money-stat-label">This week</div>
        </div>
        <div className="money-stat">
          <div className="money-stat-value">{savingsProgress}%</div>
          <div className="money-stat-label">Goal: ${savingsGoal}</div>
        </div>
      </div>
      
      <Link to="/allowance" className="card-link">
        View details <ChevronRight size={16} />
      </Link>
    </div>
  );
}

// Component: College Journey Card
function CollegeJourneyCard() {
  const navigate = useNavigate();
  
  const collegeItems = [
    {
      icon: Check,
      title: 'Complete FAFSA workshop module',
      meta: 'Due next Friday • Financial aid',
      action: () => navigate('/college')
    },
    {
      icon: AlertCircle,
      title: '5 new colleges match your preferences',
      meta: 'Liberal arts • Northeast region',
      action: () => navigate('/college')
    }
  ];
  
  return (
    <div className="sidebar-card">
      <div className="sidebar-title">
        <GraduationCap className="sidebar-icon" />
        <span>College Journey</span>
      </div>
      
      {collegeItems.map((item, index) => {
        const Icon = item.icon;
        return (
          <div key={index} className="list-item">
            <div className="list-icon" style={{ background: 'var(--color-violet-500)' }}>
              <Icon size={12} />
            </div>
            <div className="list-content">
              <div className="list-text">{item.title}</div>
              <div className="list-meta">{item.meta}</div>
            </div>
            <button className="list-action" onClick={item.action}>
              {index === 0 ? 'Start' : 'Browse'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Component: Weather Card
function WeatherCard({ environmental, onConfigureLocation }) {
  const { temperature, conditions, outdoorSuitability, weatherRecommendation, error, location } = environmental;
  
  if (error) {
    return (
      <div className="sidebar-card">
        <div className="sidebar-title">
          <Cloud className="sidebar-icon" />
          <span>Weather</span>
          <button 
            className="weather-config-btn"
            onClick={onConfigureLocation}
            title="Configure location"
          >
            <MapPin size={14} />
          </button>
        </div>
        <div className="weather-error">
          <p className="text-stone-500">Weather data unavailable</p>
          <button 
            className="empty-action"
            onClick={onConfigureLocation}
          >
            Configure location
          </button>
        </div>
      </div>
    );
  }
  
  const getWeatherIcon = (condition) => {
    switch (condition) {
      case 'clear': return Sun;
      case 'rain': return CloudRain;
      case 'clouds': return Cloud;
      default: return Cloud;
    }
  };
  
  const getOutdoorColor = (suitability) => {
    switch (suitability) {
      case 'excellent': return 'text-emerald-600';
      case 'good': return 'text-azure-600';
      case 'fair': return 'text-amber-600';
      case 'poor': return 'text-orange-600';
      case 'unsuitable': return 'text-red-600';
      default: return 'text-stone-600';
    }
  };
  
  const WeatherIcon = getWeatherIcon(environmental.weather);
  
  return (
    <div className="sidebar-card">
      <div className="sidebar-title">
        <WeatherIcon className="sidebar-icon" />
        <span>Weather</span>
        <button 
          className="weather-config-btn"
          onClick={onConfigureLocation}
          title="Configure location"
        >
          <MapPin size={14} />
        </button>
      </div>
      
      <div className="weather-main">
        <div className="weather-temp">
          <Thermometer className="temp-icon" />
          <span className="temp-value">{temperature}°F</span>
        </div>
        <div className="weather-condition">{conditions}</div>
      </div>
      
      <div className="weather-suitability">
        <div className="suitability-label">Outdoor Activities</div>
        <div className={`suitability-rating ${getOutdoorColor(outdoorSuitability)}`}>
          {outdoorSuitability.charAt(0).toUpperCase() + outdoorSuitability.slice(1)}
        </div>
      </div>
      
      <div className="weather-recommendation">
        {weatherRecommendation}
      </div>
      
      {location && (
        <div className="weather-location">
          <MapPin size={12} />
          <span>{location}</span>
        </div>
      )}
    </div>
  );
}

// Component: Smart Suggestions Card
function SmartSuggestionsCard({ suggestions, onSuggestionAction }) {
  const renderSuggestionIcon = (type) => {
    switch (type) {
      case 'timing-opportunity':
        return Book;
      case 'coordination':
        return ShoppingCart;
      case 'productivity':
        return Home;
      case 'weather-optimal':
        return Sun;
      case 'academic-urgent':
        return AlertCircle;
      case 'carpool-opportunity':
      case 'route-optimization':
      case 'conflict-resolution':
      case 'weather-alert':
        return Clock;
      default:
        return Lightbulb;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'var(--color-coral-500)';
      case 'high':
        return 'var(--color-teal-600)';
      case 'medium':
        return 'var(--color-teal-500)';
      case 'low':
        return 'var(--color-stone-400)';
      default:
        return 'var(--color-teal-500)';
    }
  };

  const formatConfidence = (confidence) => {
    if (confidence >= 0.9) return 'Very confident';
    if (confidence >= 0.7) return 'Confident';
    if (confidence >= 0.5) return 'Somewhat confident';
    return 'Learning';
  };

  const getTimingIndicator = (suggestion) => {
    if (suggestion.timingScore >= 0.8) return { text: 'Perfect time', color: 'var(--color-teal-600)' };
    if (suggestion.timingScore >= 0.6) return { text: 'Good time', color: 'var(--color-azure-600)' };
    if (suggestion.timingScore >= 0.4) return { text: 'OK time', color: 'var(--color-amber-600)' };
    return { text: 'Later might be better', color: 'var(--color-stone-500)' };
  };
  
  return (
    <div className="sidebar-card">
      <div className="sidebar-title">
        <Lightbulb className="sidebar-icon" />
        <span>Smart Suggestions</span>
      </div>
      
      {suggestions.length === 0 ? (
        <div className="empty-state">
          <p>No suggestions right now</p>
          <span className="empty-subtitle">Check back later!</span>
        </div>
      ) : (
        suggestions.map((suggestion, index) => {
          const Icon = renderSuggestionIcon(suggestion.type);
          const priorityColor = getPriorityColor(suggestion.priority);
          const confidenceText = formatConfidence(suggestion.confidence || 0.5);
          const timingIndicator = getTimingIndicator(suggestion);
          
          return (
            <div key={index} className="list-item">
              <div className="list-icon" style={{ background: priorityColor }}>
                <Icon size={12} />
              </div>
              <div className="list-content">
                <div className="list-text">
                  {suggestion.title}
                  {suggestion.optimalWindow && (
                    <span className="timing-badge">
                      <Clock size={10} />
                    </span>
                  )}
                </div>
                <div className="list-meta">
                  {suggestion.description}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
                    <span style={{ color: 'var(--color-stone-400)' }}>
                      {confidenceText} • {suggestion.timeWindow || 'Now'}
                    </span>
                    {suggestion.timingScore !== undefined && (
                      <span style={{ color: timingIndicator.color, fontWeight: '500' }}>
                        {timingIndicator.text}
                      </span>
                    )}
                  </div>
                </div>
                <div className="list-actions">
                  <button 
                    className="list-btn primary"
                    onClick={() => onSuggestionAction?.(suggestion, 'suggestion-accepted')}
                  >
                    {suggestion.type === 'timing-opportunity' ? 'Add to Plan' : 
                     suggestion.type === 'productivity' ? 'Do This' :
                     suggestion.type === 'coordination' ? 'Coordinate' :
                     suggestion.type === 'weather-optimal' ? 'Plan Activity' :
                     suggestion.type === 'carpool-opportunity' ? 'Coordinate' :
                     suggestion.type === 'route-optimization' ? 'Optimize' :
                     suggestion.type === 'conflict-resolution' ? 'Resolve' :
                     suggestion.type === 'weather-alert' ? 'Review' :
                     'Do This'}
                  </button>
                  <button 
                    className="list-btn"
                    onClick={() => onSuggestionAction?.(suggestion, 'suggestion-dismissed')}
                  >
                    Not Now
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// Component: Advanced Analytics Card
function AdvancedAnalyticsCard({ patterns }) {
  const navigate = useNavigate();
  
  // Calculate quick stats from localStorage patterns
  const getAnalyticsPreview = () => {
    try {
      const userId = JSON.parse(localStorage.getItem('currentUser'))?.id;
      if (!userId) return { dataPoints: 0, insights: 0, confidence: 0 };
      
      const userPatterns = JSON.parse(localStorage.getItem(`userPatterns_${userId}`) || '[]');
      const dataPoints = userPatterns.length;
      const recentPatterns = userPatterns.filter(p => 
        Date.now() - new Date(p.timestamp).getTime() < 7 * 24 * 60 * 60 * 1000
      );
      
      return {
        dataPoints,
        insights: Math.min(Math.floor(dataPoints / 10), 5),
        confidence: Math.min(dataPoints / 50, 1)
      };
    } catch {
      return { dataPoints: 0, insights: 0, confidence: 0 };
    }
  };
  
  const stats = getAnalyticsPreview();
  
  return (
    <div className="sidebar-card">
      <div className="sidebar-title">
        <Brain className="sidebar-icon" />
        <span>Pattern Analytics</span>
      </div>
      
      <div className="analytics-preview">
        <div className="analytics-stat">
          <div className="stat-number">{stats.dataPoints}</div>
          <div className="stat-label">Data Points</div>
        </div>
        <div className="analytics-stat">
          <div className="stat-number">{stats.insights}</div>
          <div className="stat-label">Insights Ready</div>
        </div>
      </div>
      
      {stats.confidence > 0.1 ? (
        <div className="analytics-confidence">
          <div className="confidence-bar">
            <div 
              className="confidence-fill" 
              style={{ width: `${stats.confidence * 100}%` }}
            ></div>
          </div>
          <div className="confidence-label">
            Analysis: {Math.round(stats.confidence * 100)}% ready
          </div>
        </div>
      ) : (
        <div className="analytics-building">
          <p>Building your analytics...</p>
          <span>Keep using PaperSedge to unlock insights</span>
        </div>
      )}
      
      <button 
        className="card-link"
        onClick={() => navigate('/intelligence-analytics')}
      >
        View Analytics <BarChart3 size={16} />
      </button>
    </div>
  );
}